import { QueryClient } from "@tanstack/react-query";
import {
	createTRPCClient,
	httpBatchLink,
	loggerLink,
	type TRPCLink,
} from "@trpc/client";
import {
	createTRPCContext,
	createTRPCOptionsProxy,
} from "@trpc/tanstack-react-query";
import superjson from "superjson";
import type { AppRouter } from "../router.ts";

/**
 * The typed client for the whole API.
 *
 * `AppRouter` is imported *as a type*, so nothing from the server — not the
 * router, not Drizzle, not the database driver — reaches the browser bundle;
 * `verbatimModuleSyntax` guarantees the import is erased.
 */

export const { TRPCProvider, useTRPC, useTRPCClient } =
	createTRPCContext<AppRouter>();

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * The same options proxy `useTRPC()` returns, usable outside React.
 *
 * Route loaders need it: they run before a component mounts, so they cannot
 * call a hook, but they still want `queryClient.ensureQueryData(...)` against
 * the identical query keys the component will read.
 */
export function createOptionsProxy(
	client: ApiClient,
	queryClient: QueryClient,
) {
	return createTRPCOptionsProxy<AppRouter>({ client, queryClient });
}

export type OptionsProxy = ReturnType<typeof createOptionsProxy>;

export interface CreateApiClientOptions {
	/** Origin of the API, e.g. `http://localhost:3001`. */
	readonly baseUrl: string;
	readonly development?: boolean;
}

export function createApiClient(options: CreateApiClientOptions) {
	const links: TRPCLink<AppRouter>[] = [];

	if (options.development === true) {
		links.push(
			loggerLink({
				enabled: (opts) =>
					opts.direction === "down" && opts.result instanceof Error,
			}),
		);
	}

	links.push(
		httpBatchLink({
			url: `${options.baseUrl.replace(/\/+$/, "")}/api/trpc`,
			transformer: superjson,
			// Required for the session cookie to travel cross-origin in development,
			// where the web app and the API are on different ports.
			//
			// The cast is unavoidable: this package is compiled with both Bun's and
			// the DOM's `fetch` types in scope, and their `RequestInit` definitions
			// disagree about `signal` under `exactOptionalPropertyTypes`.
			fetch: (url, init) =>
				globalThis.fetch(
					url as string,
					{ ...init, credentials: "include" } as RequestInit,
				),
		}),
	);

	return createTRPCClient<AppRouter>({ links });
}

/**
 * The shared query cache.
 *
 * `staleTime` is deliberately non-zero: the SSE stream tells the client when
 * data actually changed, so aggressive refetch-on-focus would mostly generate
 * traffic that confirms nothing moved.
 *
 * Retries skip 4xx — a 403 or a 404 will not become a 200 by asking again.
 */
export function createQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 30_000,
				gcTime: 5 * 60_000,
				refetchOnWindowFocus: false,
				retry: (failureCount, error) => {
					const status = httpStatusOf(error);
					if (status !== undefined && status >= 400 && status < 500)
						return false;
					return failureCount < 2;
				},
			},
			mutations: {
				retry: false,
			},
		},
	});
}

function httpStatusOf(error: unknown): number | undefined {
	if (typeof error !== "object" || error === null) return undefined;
	const data = (error as { data?: { httpStatus?: unknown } }).data;
	return typeof data?.httpStatus === "number" ? data.httpStatus : undefined;
}
